import { Link } from "react-router-dom";

/**
 * Shared minimal layout for legal placeholder pages (Terms / Privacy).
 * No FAB, no sidebar, no NextDropBanner — just wordmark, back-link,
 * placeholder banner, and content column.
 */
export default function LegalDocLayout({
  testid,
  title,
  effectiveDate,
  lastUpdated,
  children,
}) {
  return (
    <div
      data-testid={testid}
      className="min-h-screen w-full bg-black text-white"
    >
      {/* Top bar — wordmark left, back-link right */}
      <header className="border-b border-white/10 bg-black/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="figumatch-wordmark text-base font-semibold"
            data-testid="legal-doc-wordmark"
          >
            FiguMatch
          </Link>
          <Link
            to="/"
            data-testid="legal-doc-back-link"
            className="text-xs text-neutral-400 hover:text-amber-400 transition-colors"
          >
            ← Back to FiguMatch
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
          {title}
        </h1>
        <p className="mt-2 text-sm text-neutral-500 font-body">
          Effective date: {effectiveDate}
        </p>

        {/* Placeholder banner — subtle amber card, not alarming */}
        <div
          className="mt-6 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-100/90 font-body leading-relaxed"
          role="note"
        >
          <strong className="text-amber-300 font-semibold">
            ⚠ Placeholder document.
          </strong>{" "}
          This page contains scaffolding text only. Final{" "}
          {title.toLowerCase()} will be drafted by legal counsel before
          public launch.
        </div>

        <div className="mt-8 space-y-2">{children}</div>

        <footer className="mt-16 pt-6 border-t border-white/10 text-xs text-neutral-500 font-body space-y-2">
          <p>Last updated: {lastUpdated}</p>
          <p>
            Contact us at{" "}
            <a
              href="mailto:hello@figumatch.com"
              className="text-amber-400 hover:underline"
            >
              hello@figumatch.com
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

export function LegalSection({ heading, children }) {
  return (
    <section className="pt-4">
      <h2 className="font-display text-xl font-semibold text-white mt-8 mb-3">
        {heading}
      </h2>
      <p className="text-neutral-300 leading-relaxed font-body">
        {children}
      </p>
    </section>
  );
}
